import type { Dispatch } from '@reduxjs/toolkit'
import { TimelineEventsActions } from '@/timeline/events/store/slice'
import { TimelineReferencesActions } from '@/timeline/references/store/slice'
import { getTimelineAssetByMatter } from '../fetch/get-timeline-asset-by-matter'
import { getTimelineAssetEvents } from '../fetch/get-timeline-asset-events'
import { getTimelineAssetFiles } from '../fetch/get-timeline-asset-files'
import { TimelineAssetActions, type TimelineAssetState } from './slice'

type WorkspaceLoader = {
  dispatch: Dispatch
  getState: () => { timelineAssetState: TimelineAssetState }
  isCurrent?: () => boolean
}

/** Shared by page entry and lease recovery; force bypasses the loaded Timeline version. */
export async function loadTimelineWorkspace(
  { matterId, overviewOnly, force }: { matterId: string; overviewOnly?: boolean; force?: boolean },
  { dispatch, getState, isCurrent = () => true }: WorkspaceLoader
) {
  // A full page load must still run when the sidebar is already loading only the overview.
  if (!isCurrent() || (getState().timelineAssetState.assetLoadingByMatterId[matterId] && overviewOnly)) return
  dispatch(TimelineAssetActions.setAssetLoading({ matterId, isLoading: true }))
  dispatch(TimelineAssetActions.setAssetError({ matterId, hasError: false }))
  try {
    let response
    try {
      response = await getTimelineAssetByMatter(matterId)
    } catch (failure) {
      if (isCurrent()) dispatch(TimelineAssetActions.setAssetError({ matterId, hasError: true }))
      throw failure
    }
    if (!isCurrent()) return
    const { asset } = response
    dispatch(TimelineAssetActions.setAssetForMatter({ matterId, asset }))
    if (!asset || overviewOnly) return
    if (asset.active_update) dispatch(TimelineAssetActions.pollActiveUpdate({ assetId: asset.id, updateId: asset.active_update.id }))
    const state = getState().timelineAssetState
    if (force || state.loadedEventsVersionByAssetId[asset.id] !== asset.chronology_version || state.dataErrorByAssetId[asset.id]) {
      await loadTimelineAssetData(asset.id, dispatch, isCurrent)
    }
  } finally {
    dispatch(TimelineAssetActions.setAssetLoading({ matterId, isLoading: false }))
  }
}

/** Errors update the shared UI state and propagate so lease recovery cannot enable writes. */
export async function loadTimelineAssetData(assetId: string, dispatch: Dispatch, isCurrent = () => true) {
  if (!isCurrent()) return
  dispatch(TimelineAssetActions.setDataError({ assetId, hasError: false }))
  try {
    const [events, files] = await Promise.all([getTimelineAssetEvents(assetId), getTimelineAssetFiles(assetId)])
    if (!isCurrent()) return
    dispatch(TimelineEventsActions.setJobEventsList({ timelineJobId: assetId, events: events.events }))
    dispatch(TimelineReferencesActions.setJobReferencesRecord({ timelineJobId: assetId, references: events.references }))
    dispatch(TimelineAssetActions.setLoadedEventsVersion({ assetId, version: events.chronology_version }))
    dispatch(TimelineAssetActions.setFilesForAsset({ assetId, files: files.files }))
  } catch (failure) {
    if (isCurrent()) dispatch(TimelineAssetActions.setDataError({ assetId, hasError: true }))
    throw failure
  }
}
